import { ReactNode, useMemo, useContext } from 'react';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { selectedHeaderPalette } from './adminActionButton';
import { AppearancePendingContext } from './AppearanceBoundary';
import { defaultInterfaceTokens, interfaceTokenVariables, resolveInterfaceTokens } from './brandTokens';

/** React theme scope also reaches portaled dialogs without global document styles. */
export default function CompanyActionThemeScope({ configuration, pending = false, children }: {
  configuration?: unknown;
  pending?: boolean;
  children: ReactNode;
}) {
  const parent = useTheme();
  const inheritedPending = useContext(AppearancePendingContext);
  const blocked = inheritedPending || pending;
  const theme = useMemo(() => {
    const button = parent.components?.MuiButton;
    const base = parent.components?.MuiButtonBase;
    const tokens = resolveInterfaceTokens(configuration);
    const customized = tokens.actionFill !== defaultInterfaceTokens.actionFill || tokens.actionText !== defaultInterfaceTokens.actionText || tokens.actionBorder !== defaultInterfaceTokens.actionBorder;
    const variables = configuration === undefined ? {} : {
      ...interfaceTokenVariables(tokens),
      '--contract-tab-fill': customized ? tokens.actionFill : selectedHeaderPalette.text,
      '--contract-tab-text': customized ? tokens.actionText : parent.palette.text.primary,
      '--contract-tab-border': customized ? tokens.actionBorder : selectedHeaderPalette.border
    };
    const readiness = blocked ? { visibility: 'hidden !important', '& *': { visibility: 'hidden !important' } } : {};
    return {
      ...parent,
      components: {
        ...parent.components,
        MuiButtonBase: {
          ...base,
          styleOverrides: { ...base?.styleOverrides, root: [base?.styleOverrides?.root, variables, readiness] }
        },
        MuiButton: {
          ...button,
          styleOverrides: {
            ...button?.styleOverrides,
            root: [button?.styleOverrides?.root, variables, readiness]
          }
        }
      }
    };
  }, [parent, configuration, blocked]);
  return <AppearancePendingContext.Provider value={blocked}><ThemeProvider theme={theme}>{children}</ThemeProvider></AppearancePendingContext.Provider>;
}
